/**
 * Matriz del cuadro de turnos (008 · T045 · US1; contracts/ui.md §1.2, FR-006).
 *
 * Patrón ARIA `grid` con selección múltiple: columna fija "Trabajador" (nombre + patrón activo en
 * 14 px Neutro 600) y una columna por día (encabezado "lu 01"; feriado con icono `party-popper` y
 * su nombre accesible). Una sola celda es tabulable (foco "roving"); las flechas, Inicio/Fin,
 * Espacio, Shift+Espacio, Enter (historial) y Escape se manejan aquí. La selección vive en
 * `use-grid-selection.tsx`.
 */
import { PartyPopper } from 'lucide-react';
import { useCallback, useRef, type KeyboardEvent } from 'react';

import type { ScheduleGridDay, ScheduleGridResponse } from '@fermin/contracts';

import { t } from '../../../i18n';
import { cn } from '../../../lib/cn';
import { dayOfMonth, weekdayLong, weekdayShort } from '../format';

import { GridCell } from './grid-cell';
import { cellKey, type GridSelection } from './use-grid-selection';

export type ScheduleGridRow = ScheduleGridResponse['rows'][number];

export interface ScheduleGridProps {
  days: readonly ScheduleGridDay[];
  rows: readonly ScheduleGridRow[];
  selection: GridSelection;
  isSelectable: (key: string) => boolean;
  errors: ReadonlyMap<string, string>;
  onOpenHistory: (workerId: string, day: string) => void;
}

function positionKey(row: number, col: number): string {
  return `${row}:${col}`;
}

export function ScheduleGrid({
  days,
  rows,
  selection,
  isSelectable,
  errors,
  onOpenHistory,
}: ScheduleGridProps) {
  const cellRefs = useRef(new Map<string, HTMLTableCellElement>());

  const registerCell = useCallback(
    (row: number, col: number) => (element: HTMLTableCellElement | null) => {
      const key = positionKey(row, col);
      if (element) {
        cellRefs.current.set(key, element);
      } else {
        cellRefs.current.delete(key);
      }
    },
    [],
  );

  function focusAt(row: number, col: number) {
    cellRefs.current.get(positionKey(row, col))?.focus();
  }

  function onKeyDown(event: KeyboardEvent<HTMLTableCellElement>, row: number, col: number) {
    const moves: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    const move = moves[event.key];
    if (move) {
      event.preventDefault();
      const next = { row: row + move[0], col: col + move[1] };
      const clamped = {
        row: Math.max(0, Math.min(next.row, rows.length - 1)),
        col: Math.max(0, Math.min(next.col, days.length - 1)),
      };
      selection.setFocus(clamped);
      focusAt(clamped.row, clamped.col);
      if (event.shiftKey) {
        selection.extendTo(clamped);
      }
      return;
    }
    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      const target = { row, col: event.key === 'Home' ? 0 : days.length - 1 };
      selection.setFocus(target);
      focusAt(target.row, target.col);
      return;
    }
    if (event.key === ' ' || event.key === 'Spacebar') {
      event.preventDefault();
      if (event.shiftKey) {
        selection.extendTo({ row, col });
      } else {
        selection.toggle({ row, col });
      }
      return;
    }
    if (event.key === 'Enter') {
      event.preventDefault();
      const worker = rows[row]?.worker.id;
      const day = days[col]?.day;
      if (worker && day) {
        onOpenHistory(worker, day);
      }
      return;
    }
    if (event.key === 'Escape' && selection.selected.size > 0) {
      event.preventDefault();
      selection.clear();
    }
  }

  return (
    <div className="w-full overflow-x-auto rounded-fm-lg border border-fm-border bg-fm-surface">
      <table
        role="grid"
        aria-label={t('schedules.grid.label')}
        aria-multiselectable="true"
        aria-rowcount={rows.length + 1}
        aria-colcount={days.length + 1}
        data-testid="schedule-grid"
        className="border-collapse text-fm-sm text-fm-text"
      >
        <thead>
          <tr>
            <th
              scope="col"
              className="sticky left-0 z-20 min-w-[220px] border-b border-fm-border bg-fm-surface px-fm-3 text-left text-fm-xs font-medium uppercase tracking-[0.04em] text-fm-text-secondary"
            >
              {t('schedules.grid.worker')}
            </th>
            {days.map((day) => {
              const holiday = day.holiday_names.join(', ');
              return (
                <th
                  key={day.day}
                  scope="col"
                  data-day={day.day}
                  className={cn(
                    'h-fm-touch w-fm-touch min-w-fm-touch border-b border-fm-border px-0 text-center text-fm-xs font-medium',
                    day.weekday >= 6 ? 'text-fm-primary-text' : 'text-fm-text-secondary',
                  )}
                >
                  <span className="sr-only">
                    {`${weekdayLong(day.weekday)} ${Number(dayOfMonth(day.day))}`}
                    {holiday ? `, ${t('schedules.grid.holidayHeader', { name: holiday })}` : ''}
                  </span>
                  <span aria-hidden="true" className="flex flex-col items-center leading-tight">
                    <span>{weekdayShort(day.weekday)}</span>
                    <span className="fm-tabular">{dayOfMonth(day.day)}</span>
                    {holiday ? (
                      <PartyPopper
                        size={12}
                        className="text-fm-info-text"
                        data-testid="schedule-grid-holiday"
                      />
                    ) : null}
                  </span>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => {
            const cells = new Map(row.cells.map((cell) => [cell.day, cell]));
            return (
              <tr key={row.worker.id} data-testid="schedule-grid-row">
                <th
                  scope="row"
                  className="sticky left-0 z-10 min-w-[220px] border-b border-fm-border bg-fm-surface px-fm-3 py-fm-1 text-left font-normal"
                >
                  <span className="block truncate text-fm-sm font-medium text-fm-text">
                    {row.worker.name}
                  </span>
                  <span className="block truncate text-fm-sm text-fm-text-secondary">
                    {row.active_pattern
                      ? t('schedules.grid.activePattern', { name: row.active_pattern.name })
                      : (row.worker.internal_code ?? '')}
                  </span>
                </th>
                {days.map((day, colIndex) => {
                  const key = cellKey(row.worker.id, day.day);
                  return (
                    <GridCell
                      key={day.day}
                      cell={cells.get(day.day)}
                      workerId={row.worker.id}
                      day={day.day}
                      weekday={day.weekday}
                      row={rowIndex}
                      col={colIndex}
                      selected={selection.selected.has(key)}
                      focused={selection.focus.row === rowIndex && selection.focus.col === colIndex}
                      selectable={isSelectable(key)}
                      error={errors.get(key)}
                      cellRef={registerCell(rowIndex, colIndex)}
                      onPointerDown={(r, c, shift) =>
                        selection.pointerDown({ row: r, col: c }, shift)
                      }
                      onPointerEnter={(r, c) => selection.pointerEnter({ row: r, col: c })}
                      onFocusCell={(r, c) => selection.setFocus({ row: r, col: c })}
                      onKeyDown={onKeyDown}
                      onOpenHistory={onOpenHistory}
                    />
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
